import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  ConfirmDialog,
  DateInput,
  Drawer,
  EmptyState,
  FormField,
  GlassCard,
  InfoBanner,
  Input,
  MoneyInput,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import {
  FieldRow,
  NumericFieldRow,
} from '../../../components/common/field_row';
import { PAY_FREQUENCY_KEY } from '../../../lib/label_keys';
import { worker_type_capabilities } from '../../../lib/worker_types';
import {
  format_basis_points,
  format_date,
  format_money,
} from '../../../lib/format';
import type { Employee } from '../../../data/types';
import { localise_fixture } from '../../../data/mock_labels';
import {
  useEmployeeDeductions,
  type DeductionType,
} from '../../../data/use_employee_deductions';
import { useSettings } from '../../../data/use_settings';
import {
  DEDUCTION_TYPES,
  buildAddDeductionInput,
  defaultDeductionFormState,
  isDeductionFormValid,
  validateDeductionForm,
  type DeductionFormState,
} from './payroll_tab_validation';
import { LeaveBalancesPanel } from '../leave_balances_panel';
import { format_pay_amount } from '../pay_amount_display';
import type { UseEmployeeCompensationResult } from '../../../data/use_employee_compensation';
import { SalaryHistoryCard } from './salary_history_card';
import { ApitBasisRows } from './apit_basis_rows';

const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

export function PayrollTab({
  employee,
  compensation,
}: {
  employee: Employee;
  /**
   * The profile screen's single `useEmployeeCompensation` instance -- shared
   * with the Change salary dialog so a change made there refreshes the
   * history card below without a second fetch.
   */
  compensation: UseEmployeeCompensationResult;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const {
    deductions,
    is_loading,
    state,
    retry,
    add_deduction,
    remove_deduction,
  } = useEmployeeDeductions(employee.id);
  const { organisation } = useSettings();

  const [drawer_open, set_drawer_open] = useState(false);
  const [form, set_form] = useState<DeductionFormState>(
    defaultDeductionFormState(),
  );
  const [saving, set_saving] = useState(false);
  const [add_error, set_add_error] = useState(false);
  const [removing_id, set_removing_id] = useState<string | null>(null);
  const [remove_loading, set_remove_loading] = useState(false);
  const [remove_error, set_remove_error] = useState(false);

  const errors = validateDeductionForm(form);
  const form_valid = isDeductionFormValid(form);

  // An hourly employee is paid a rate against recorded time, not a fixed
  // per-period salary: showing them `basic_salary` printed "LKR 0" and hid
  // the rate they are actually paid at entirely (#24396).
  const is_hourly = employee.pay_basis === 'hourly';

  // Every amount on this card is in the organisation's base currency —
  // never a hard-coded 'LKR', which printed an Indonesian entity's IDR rate
  // as "LKR 1,200". Same source `LeaveBalancesPanel` below already reads.
  const currency = organisation.base_currency;
  // The salary / hourly rate is in the employee's own pay currency (NULL on
  // the record = the base currency); deductions and leave stay in the base.
  const pay_currency = employee.pay_currency_code ?? currency;

  const deduction_type_labels: Record<DeductionType, string> = {
    tax: t('employees.deduction_type.tax'),
    superannuation: t('employees.deduction_type.superannuation'),
    court_order: t('employees.deduction_type.court_order'),
    other: t('employees.deduction_type.other'),
  };

  const deduction_type_options: StyledSelectOption<DeductionType>[] =
    DEDUCTION_TYPES.map((dt) => ({
      value: dt,
      label: deduction_type_labels[dt],
    }));

  function open_drawer(): void {
    set_form(defaultDeductionFormState());
    set_add_error(false);
    set_drawer_open(true);
  }

  async function handle_save(): Promise<void> {
    if (!add_deduction) return;
    set_saving(true);
    set_add_error(false);
    const result = await add_deduction(buildAddDeductionInput(form));
    set_saving(false);
    if (result.outcome === 'success') {
      set_drawer_open(false);
      return;
    }
    set_add_error(true);
  }

  // An independent contractor is never on payroll: no pay components,
  // standing (statutory) deductions or leave balances apply — the API
  // excludes them from pay runs and refuses leave actions. Say so plainly
  // instead of rendering sections that could never be used.
  if (!worker_type_capabilities(employee.employment_type).on_payroll) {
    return (
      <div data-testid="employee-payroll-contractor-note">
        <InfoBanner title={t('employees.contractor_not_on_payroll_title')}>
          {t('employees.contractor_not_on_payroll_body')}
        </InfoBanner>
      </div>
    );
  }

  return (
    <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
      <GlassCard className="rounded-2xl p-5">
        <h2 className="text-[14.5px] font-bold mb-1.5">
          {t('employees.pay_components')}
        </h2>
        <FieldRow label={t('employees.pay_frequency')}>
          {t(PAY_FREQUENCY_KEY[employee.pay_frequency])}
        </FieldRow>
        <FieldRow label={t('employees.form_pay_basis')}>
          {is_hourly
            ? t('employees.form_pay_basis_hourly')
            : t('employees.form_pay_basis_salaried')}
        </FieldRow>
        {is_hourly ? (
          <NumericFieldRow label={t('employees.form_hourly_rate')}>
            {format_pay_amount(employee.hourly_rate, pay_currency, locale)}
          </NumericFieldRow>
        ) : (
          <NumericFieldRow label={t('employees.basic_salary')}>
            {format_pay_amount(employee.basic_salary, pay_currency, locale)}
          </NumericFieldRow>
        )}
        <FieldRow label={t('employees.salary_structure')}>
          {localise_fixture(employee.salary_structure, locale)}
        </FieldRow>
        <ApitBasisRows employee={employee} baseCurrency={currency} />
      </GlassCard>

      {/*
        Always rendered: an employee that predates salary history (or was
        never edited) gets an empty state that says the current pay is the
        only value on record, rather than a card that appears and vanishes.
      */}
      <SalaryHistoryCard compensation={compensation} currency={currency} />

      <GlassCard className="rounded-2xl p-5 lg:col-span-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
          <h2 className="text-[14.5px] font-bold">
            {t('employees.standing_deductions')}
          </h2>
          {add_deduction !== undefined && (
            <Button variant="primary" size="sm" onClick={open_drawer}>
              {t('employees.add_deduction')}
            </Button>
          )}
        </div>

        {is_loading && <div className="nv-skel" style={{ height: 48 }} />}

        {state.status === 'entitlement_required' && (
          <EmptyState
            title={t('data_centre.entitlement_required_title')}
            description={t('data_centre.entitlement_required_description')}
          />
        )}

        {state.status === 'error' && (
          <EmptyState
            title={t('employees.deductions_load_error')}
            action={
              <Button variant="secondary" size="sm" onClick={retry}>
                {t('employees.deductions_retry')}
              </Button>
            }
          />
        )}

        {state.status === 'ready' && deductions.length === 0 && (
          <EmptyState
            title={t('employees.no_deductions')}
            description={t('employees.no_deductions_description')}
          />
        )}

        {state.status === 'ready' && deductions.length > 0 && (
          <table className="nv-table w-full text-[13px]">
            <thead>
              <tr>
                <th>{t('employees.deduction_type')}</th>
                <th>{t('employees.deduction_amount')}</th>
                <th>{t('employees.deduction_effective_from')}</th>
                <th>{t('employees.deduction_effective_to')}</th>
                <th>{t('employees.deduction_reference')}</th>
                {remove_deduction !== undefined && <th />}
              </tr>
            </thead>
            <tbody>
              {deductions.map((d) => (
                <tr key={d.id}>
                  <td>{deduction_type_labels[d.deduction_type]}</td>
                  <td className="num">
                    {d.is_percentage
                      ? format_basis_points(d.amount_cents_or_bps, locale)
                      : format_money(d.amount_cents_or_bps, currency, locale)}
                  </td>
                  <td>{format_date(d.effective_from, locale)}</td>
                  <td>
                    {d.effective_to
                      ? format_date(d.effective_to, locale)
                      : t('employees.deduction_effective_to_open')}
                  </td>
                  <td>{d.reference ?? '—'}</td>
                  {remove_deduction !== undefined && (
                    <td>
                      <Button
                        variant="ghost-danger"
                        size="sm"
                        onClick={() => {
                          set_removing_id(d.id);
                          set_remove_error(false);
                        }}
                      >
                        {t('employees.remove_deduction')}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassCard>

      <LeaveBalancesPanel
        employee_id={employee.id}
        employee_name={employee.name}
        currency={organisation.base_currency}
      />

      <Drawer
        open={drawer_open}
        onClose={() => {
          set_drawer_open(false);
        }}
        title={t('employees.add_deduction_title')}
      >
        <div className="flex flex-col gap-3">
          <FormField label={t('employees.deduction_type')}>
            <StyledSelect
              value={form.deduction_type}
              onChange={(value) => {
                set_form((prev) => ({ ...prev, deduction_type: value }));
              }}
              options={deduction_type_options}
              ariaLabel={t('employees.deduction_type')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
            />
          </FormField>

          <FormField
            label={t('employees.deduction_amount')}
            htmlFor="deduction-amount"
            error={
              errors.amount
                ? t('employees.deduction_amount_required')
                : undefined
            }
          >
            <MoneyInput
              id="deduction-amount"
              value={
                form.amount_text === ''
                  ? undefined
                  : Math.round(parseFloat(form.amount_text) * 100)
              }
              blankZero
              onChange={(cents) => {
                set_form((prev) => ({
                  ...prev,
                  amount_text: (cents / 100).toString(),
                }));
              }}
            />
          </FormField>

          <FormField
            label={t('employees.deduction_effective_from')}
            htmlFor="deduction-effective-from"
            error={
              errors.effective_from
                ? t('employees.deduction_effective_from_required')
                : undefined
            }
          >
            <DateInput
              id="deduction-effective-from"
              value={form.effective_from}
              locale={locale}
              onChange={(e) => {
                set_form((prev) => ({
                  ...prev,
                  effective_from: e.target.value,
                }));
              }}
            />
          </FormField>

          <FormField
            label={t('employees.deduction_effective_to')}
            htmlFor="deduction-effective-to"
          >
            <DateInput
              id="deduction-effective-to"
              value={form.effective_to}
              locale={locale}
              onChange={(e) => {
                set_form((prev) => ({ ...prev, effective_to: e.target.value }));
              }}
            />
          </FormField>

          <FormField
            label={t('employees.deduction_reference')}
            htmlFor="deduction-reference"
          >
            <Input
              id="deduction-reference"
              value={form.reference}
              placeholder={t('employees.deduction_reference_placeholder')}
              onChange={(e) => {
                set_form((prev) => ({ ...prev, reference: e.target.value }));
              }}
            />
          </FormField>

          {add_error && (
            <p className="nv-help is-error" role="alert">
              {t('employees.add_deduction_error')}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              disabled={!form_valid || saving}
              loading={saving}
              onClick={() => void handle_save()}
            >
              {t('employees.save_deduction')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => {
                set_drawer_open(false);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={removing_id !== null}
        onOpenChange={(open) => {
          if (!open) {
            set_removing_id(null);
            set_remove_loading(false);
            set_remove_error(false);
          }
        }}
        title={t('employees.remove_deduction_confirm_title')}
        description={t('employees.remove_deduction_confirm_description')}
        confirmLabel={t('employees.remove_deduction')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={remove_loading}
        onConfirm={() => {
          if (removing_id === null || remove_deduction === undefined) return;
          const id = removing_id;
          set_remove_loading(true);
          set_remove_error(false);
          void remove_deduction(id).then((result) => {
            set_remove_loading(false);
            if (result.outcome === 'success') {
              set_removing_id(null);
            } else {
              set_remove_error(true);
            }
          });
        }}
      >
        {remove_error && (
          <p role="alert" className="nv-help is-error">
            {t('employees.remove_deduction_error')}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
